/**
 * ReportingView.jsx - the Reporting tab: KPIs, bid history, pipeline, win
 * rates, utilisation and the ad-hoc chart builder for the selected year.
 *
 * Every figure is derived from the same projects data the rest of the app
 * uses, cleaned through ./reporting/reportingHelpers - see that file for why
 * labels are normalised before anything is grouped.
 *
 * The figures are worked out in ./reporting/useReportingHelpers,
 * useReportingSections and usePmCharts; each section's JSX is a render
 * function in ./reporting/sections/ called with exactly the values it reads
 * (moved verbatim, so the DOM is unchanged). Giving a section real
 * props of its own is a redesign: do it one section at a time.
 */
import { useState, useMemo, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { useCalendarUtilization } from '../hooks/useCalendarUtilization';
import { capacityHeadcount } from '../components/charts/UtilizationForecastChart';
import { availableHours } from '../utils/utilizationFromCalendar';
import { SectionHeader } from './reporting/SectionHeader';
import { VizWidget } from './reporting/VizWidget';
import CogsRollup from './reporting/CogsRollup';
import { hasArea } from '../data/roles';
import { useMyAccess } from '../hooks/useMyAccess';
import { useReportingHelpers } from './reporting/useReportingHelpers';
import { useReportingSections } from './reporting/useReportingSections';
import { usePmCharts } from './reporting/usePmCharts';
import { renderKpiRow } from './reporting/sections/KpiRow';
import { renderBidsByYear } from './reporting/sections/BidsByYear';
import { renderPipelineWinRate } from './reporting/sections/PipelineWinRate';
import { renderRevenueTrend } from './reporting/sections/RevenueTrend';
import { renderUtilization } from './reporting/sections/Utilization';
import { renderStatusCapacity } from './reporting/sections/StatusCapacity';
import { renderPmFocus } from './reporting/sections/PmFocus';
import { renderTopOpenBids } from './reporting/sections/TopOpenBids';
import { renderStickyFooter } from './reporting/sections/StickyFooter';

/* ===== MAIN VIEW ===== */
export default function ReportingView({ onNavigate }) {
  const { allProjects, projectsThisYear, allEngineers, selectedYear } = useApp();
  // COGS & margin by project: the 'cost-rates' area only (10/12/26)
  const seesCogs = hasArea(useMyAccess(), 'cost-rates', 'view');
  const yearLabel = selectedYear === 'all' ? 'All Time' : Array.isArray(selectedYear) ? selectedYear.join(' + ') : String(selectedYear);

  // Filter out blank/incomplete records (no customer)
  const validProjects = useMemo(() => allProjects.filter((p) => p.customer && p.customer.trim()), [allProjects]);

  // -- CHART REFS (for PNG export) --
  const bidHistRef = useRef(null);
  const pipelineRef = useRef(null);
  const wrCustRef = useRef(null);
  const revTrendRef = useRef(null);
  const donutRef = useRef(null);
  const capacityRef = useRef(null);
  const wrAcctRef = useRef(null);

  // -- FILTER STATE (only for multi-year aggregate charts) --
  const [bidHistFilter, setBidHistFilter] = useState('byyear');
  const [bidHistCustomFrom, setBidHistCustomFrom] = useState('');
  const [bidHistCustomTo, setBidHistCustomTo] = useState('');

  const [revFilter, setRevFilter] = useState('byyear');
  const [revCustomFrom, setRevCustomFrom] = useState('');
  const [revCustomTo, setRevCustomTo] = useState('');

  const {
    data: utilizationData,
    loaded: utilizationLoaded,
    failed: calendarFailed,
    edits: calendarEdits,
    details: calendarDetails,
    layout: calendarLayout,
  } = useCalendarUtilization();
  const [forecastMode, setForecastMode] = useState('hours'); // 'hours' | 'percent'

  // The Utilization Forecast's Available line counts the same people and days
  // as the utilization chart: NA, no interns, weekdays, less each person's PTO.
  // This month's figure is quoted under the chart so the caption and the line
  // are one number.
  const forecastHeadcount = useMemo(() => capacityHeadcount(allEngineers, calendarLayout), [allEngineers, calendarLayout]);
  const capacityNow = useMemo(() => {
    const now = new Date();
    return availableHours({
      engineers: allEngineers,
      edits: calendarEdits,
      details: calendarDetails,
      layout: calendarLayout,
      year: now.getFullYear(),
      monthIndex: now.getMonth(),
    });
  }, [allEngineers, calendarEdits, calendarDetails, calendarLayout]);

  const { valByField, countByField } = useReportingHelpers({ allEngineers });

  const {
    kpiOpenValue,
    kpiSets,
    kpiWonValue,
    kpiLostValue,
    kpiWinMargin,
    kpiWonCogs,
    kpiWinRate,
    bidsByYear,
    avgWinsPerYear,
    pipelineByCustomer,
    winRateByCustomer,
    revTrendData,
    vizDatasets,
    statusTotal,
    statusDonut,
  } = useReportingSections({
    projectsThisYear,
    allEngineers,
    validProjects,
    bidHistFilter,
    bidHistCustomFrom,
    bidHistCustomTo,
    allProjects,
    valByField,
    revFilter,
    revCustomFrom,
    revCustomTo,
    countByField,
  });

  const {
    handleVizExport,
    openBidAging,
    agingBuckets,
    winRateByAccount,
    marginByCustomer,
    quoteToPoStats,
    topBidsSets,
    topBids,
    topBidsTotal,
  } = usePmCharts({ projectsThisYear });

  // -- RENDER -------------------------------------------------
  if (allProjects.length === 0) {
    /* Initial-load skeleton mirrors the real layout so nothing jumps on arrival */
    return (
      <div aria-busy="true" aria-label="Loading reporting" style={{ paddingBottom: 60 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 20 }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="go-skel" style={{ height: 104 }} />
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
          <div className="go-skel" style={{ height: 300 }} />
          <div className="go-skel" style={{ height: 300 }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} className="go-skel" style={{ height: 260 }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 60 }}>
      {renderKpiRow({
        kpiOpenValue,
        kpiSets,
        yearLabel,
        kpiWonValue,
        kpiLostValue,
        kpiWinMargin,
        kpiWonCogs,
        kpiWinRate,
        projectsThisYear,
        validProjects,
      })}

      {renderBidsByYear({
        bidHistFilter,
        setBidHistFilter,
        bidHistCustomFrom,
        bidHistCustomTo,
        setBidHistCustomFrom,
        setBidHistCustomTo,
        bidsByYear,
        bidHistRef,
        avgWinsPerYear,
      })}

      {renderPipelineWinRate({ yearLabel, pipelineByCustomer, pipelineRef, winRateByCustomer, wrCustRef })}

      {renderRevenueTrend({
        revFilter,
        setRevFilter,
        revCustomFrom,
        revCustomTo,
        setRevCustomFrom,
        setRevCustomTo,
        revTrendData,
        revTrendRef,
      })}

      {/* VISUALIZATION STUDIO */}
      <SectionHeader title="Data Visualization Studio" subtitle={`Build custom charts from live pipeline data · ${yearLabel}`} />
      <div style={{ marginBottom: 20 }}>
        <VizWidget datasets={vizDatasets} onExport={handleVizExport} />
      </div>

      {renderUtilization({
        calendarFailed,
        utilizationLoaded,
        utilizationData,
        forecastHeadcount,
        capacityNow,
        setForecastMode,
        forecastMode,
        allEngineers,
        calendarEdits,
        calendarDetails,
        calendarLayout,
      })}

      {renderStatusCapacity({
        statusTotal,
        yearLabel,
        statusDonut,
        donutRef,
        openBidAging,
        capacityRef,
        agingBuckets,
        winRateByAccount,
        wrAcctRef,
      })}

      {renderPmFocus({ yearLabel, marginByCustomer, quoteToPoStats, openBidAging, onNavigate })}

      {seesCogs && (
        <CogsRollup
          onOpenProject={(id) => {
            window.location.hash = `#/projects?project=${encodeURIComponent(id)}`;
          }}
        />
      )}

      {renderTopOpenBids({ topBidsSets, yearLabel, selectedYear, topBids, topBidsTotal })}

      {renderStickyFooter({
        yearLabel,
        kpiOpenValue,
        kpiWonValue,
        kpiLostValue,
        kpiWinMargin,
        kpiWinRate,
        projectsThisYear,
        bidsByYear,
        pipelineByCustomer,
        winRateByCustomer,
        revTrendData,
        statusDonut,
        topBids,
        selectedYear,
      })}
    </div>
  );
}
